"use client"
 
import { useState, useMemo, useEffect } from "react"
import { createPortal } from "react-dom"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import { getCookie, setCookie } from "../../utils/cookies"
import { Button } from "../../components/ui/button"
import api from "../../utils/api"
import { Input } from "../../components/ui/input"

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select"
import {
  Dialog,
  DialogContent,
} from "../../components/ui/dialog"
import {
  ArrowLeft,
  Globe,
  MapPin,
  Building2,
  ChevronLeft,
  ChevronRight,
  X,
  User
} from "lucide-react"
import { format, addDays } from "date-fns"

const getImageUrl = (imagePath: string) => {
  if (!imagePath) return ""
  const filename = imagePath.split("/").pop() || ""
  if (!filename) return ""
  return `https://s3.ap-south-1.amazonaws.com/images.prosoft.com/${filename}`
}
 
export default function DoctorBookingPage() {
const formatTime = (time: string) => {
  if (!time) return ""

  const [h, m] = time.split(":")
  let hour = parseInt(h)
  const ampm = hour >= 12 ? "PM" : "AM"

  hour = hour % 12 || 12

  return `${hour}:${m} ${ampm}`
}

  const navigate = useNavigate()
  const { doctorSlug } = useParams()
  const [searchParams] = useSearchParams()
  const doctorId = searchParams.get("doctorid") || searchParams.get("doctorId") || ""
  const doctorSlugParam = searchParams.get("doctor") || ""
  const typeParam = searchParams.get("type") || ""
  const [availableDates, setAvailableDates] = useState<any[]>([])
  const [timeSlots, setTimeSlots] = useState<any[]>([])
  const [selectedDoctor, setSelectedDoctor] = useState<any | null>(null)
  const [branches, setBranches] = useState<{ id: string; name: string }[]>([])
  const [selectedBranch, setSelectedBranch] = useState("")
  const [dateOffset, setDateOffset] = useState(0)
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date())
  const [selectedSlot, setSelectedSlot] = useState("")
  const [selectedSlotRaw, setSelectedSlotRaw] = useState("")
  const [consultationType, setConsultationType] = useState<"hospital" | "video">(typeParam === "video" ? "video" : "hospital")
  const [loggedInUser, setLoggedInUser] = useState<any>(null)
  const [showPhonePopup, setShowPhonePopup] = useState(false)
  const [showRegistrationPopup, setShowRegistrationPopup] = useState(false)
  const [showOtpPopup, setShowOtpPopup] = useState(false)
  const [showNotFoundPopup, setShowNotFoundPopup] = useState(false)
  const [signupMode, setSignupMode] = useState(false)
  const [otpError, setOtpError] = useState("")
  const [showPaymentPopup, setShowPaymentPopup] = useState(false)
  const [showThankYou, setShowThankYou] = useState(false)
  const [mobileNumber, setMobileNumber] = useState("")
  const [phoneError, setPhoneError] = useState("")
  const [otpValue, setOtpValue] = useState("")
  const [selectedPatientForPayment, setSelectedPatientForPayment] = useState<{id:number;name:string;uid:string}|null>(null)
  const [consultationPrice, setConsultationPrice] = useState<{visitType:string;visitTypeName:string;consultationCharge:number}|null>(null)
  const [priceLoading, setPriceLoading] = useState(false)
  const [payDetails, setPayDetails] = useState<any>(null)
  const [payLoading, setPayLoading] = useState(false)
  const [payMode, setPayMode] = useState<"online"|"hospital"|null>(null)
  const [showPatientSelectPopup, setShowPatientSelectPopup] = useState(false)
  const [selectedPatient, setSelectedPatient] = useState<{id:number;name:string;uid:string;regDate:string}|null>(null)
  const [savedPatients, setSavedPatients] = useState<{id:number;name:string;uid:string;regDate:string}[]>([])
 
  const [registrationData, setRegistrationData] = useState({
    firstName: "",
    lastName: "",
    dob: "",
    relation: "",
    gender: "Male",
    stateId: "",
  })
  const [relations, setRelations] = useState<{id:string;name:string}[]>([])
  const [states, setStates] = useState<{id:string;name:string}[]>([])

  useEffect(() => {
    api.get("/apis/web/relations")
      .then(res => {
        if (res.data?.success && Array.isArray(res.data.data?.records)) {
          setRelations(res.data.data.records.map((r: any) => ({ id: r.RELATION_ID, name: r.RELATION_NAME })))
        }
      })
      .catch(() => {})
    api.get("/apis/web/states")
      .then(res => {
        if (res.data?.success && Array.isArray(res.data.data)) {
          setStates(res.data.data.map((s: any) => ({ id: s.state_id, name: s.state_name })))
        }
      })
      .catch(() => {})
  }, [])

  const [timer, setTimer] = useState(30)
  useEffect(() => {
  if (timer <= 0) return

  const interval = setInterval(() => {
    setTimer((prev) => prev - 1)
  }, 1000)

  return () => clearInterval(interval)
}, [timer])

useEffect(() => {
  const paymentStatus = searchParams.get("payment")
  const razorpayPaymentId = searchParams.get("razorpay_payment_id")
  const razorpayOrderId = searchParams.get("razorpay_order_id")
  const razorpaySignature = searchParams.get("razorpay_signature")

  if (paymentStatus === "success" && razorpayPaymentId) {
    const stored = sessionStorage.getItem("payDetails")
    if (stored) {
      const details = JSON.parse(stored)
      setPayDetails({
        ...details,
        razorpayPaymentId,
        razorpayOrderId,
        razorpaySignature,
        paymentStatus: "Paid",
      })
      sessionStorage.removeItem("payDetails")
      setShowThankYou(true)
    }
  }
}, [])

useEffect(() => {
  if (availableDates.length > 0) {
    const today = new Date()

    const matchedDate = availableDates.find((item) => {
      const d = new Date(item.dateTime)
      return d.toDateString() === today.toDateString()
    })

    if (matchedDate) {
      setSelectedDate(new Date(matchedDate.dateTime))
    } else {
      // fallback → select first date
      setSelectedDate(new Date(availableDates[0].dateTime))
    }
  }
}, [availableDates])
 
const fetchPatientsByMobile = async (mobile: string) => {
  try {
    const res = await api.get(`/apis/web/patientbymobilename?mobile=91${mobile}`);
    if (res.data.status === 0 && Array.isArray(res.data.result)) {
      return res.data.result.map((p: any, index: number) => ({
        id: index + 1,
        name: p.patient_name,
        uid: p.patientmr_no,
        regDate: p.Created_on ? format(new Date(p.Created_on), "dd/MM/yyyy") : ""
      }));
    }
  } catch (err) {
    console.error("Failed to fetch patients list", err);
  }
  return [];
}

useEffect(() => {
  const savedUser = getCookie("user")
  if (savedUser && savedUser !== "undefined") {
    try {
      const u = JSON.parse(savedUser)
      setLoggedInUser(u)
      const phone = u.phone || u.mobile || u.mobileNo || ""
      if (phone) {
        const cleanPhone = phone.replace(/\D/g, "").replace(/^91/, "")
        fetchPatientsByMobile(cleanPhone).then(patients => setSavedPatients(patients))
      }
    } catch (e) {}
  }
 
  const handleUserUpdated = (e: any) => {
    const u = e.detail
    setLoggedInUser(u)
    if (!u) {
      setSavedPatients([])
    }
  }
  window.addEventListener("user-updated", handleUserUpdated)
  return () => window.removeEventListener("user-updated", handleUserUpdated)
}, [])
 
useEffect(() => {
  if (doctorId) {
 
    // ✅ 1. OLD API (Doctor Profile)
    api.get(`/apis/web/doctorprofiles?doctorId=${doctorId}`)
      .then(res => {
        const d = res.data.result?.[0]
        if (!d) return
 
        setSelectedDoctor({
          id: d.DOCTORID,
          name: d.DOCTOR,
          specialty: d.Speciality_Name,
          specialtyId: d.Speciality_Name?.toLowerCase().replace(/\s+/g, "-"),
          designation: d.Speciality_Name,
          qualifications: d.QUALIFICATION,
          languages: d.LANG ? d.LANG.split(",").map((l: string) => l.trim()) : [],
          image: getImageUrl(d.IMAGE_PATH),
          slug: doctorSlug || doctorId,
        })
      })
      .catch(err => console.error(err))
 
 
    // ✅ 2. NEW API (Locations / Branches)
    api.get(`/apis/web/locationsbydoctor?doctorId=${doctorId}`)
      .then(res => {
        const locations = res.data.result || []
      const branchList = locations.map((loc: any) => ({
        id: loc.Hospital_Id,
        name: loc.HospitalName
      }))
 
        setBranches(branchList)
 
        if (branchList.length > 0) {
          setSelectedBranch(branchList[0].id)
        }
      })
      .catch(err => console.error(err))
 
  }
}, [doctorId])
 
  const resolvedDoctorId = selectedDoctor?.id || doctorId
 
  useEffect(() => {
    if (!resolvedDoctorId || !selectedBranch) return
    setSelectedDate(null)
    setTimeSlots([])
    setDateOffset(0)
    api.get(`/apis/web/doctoravaildates?clnorgcode=${selectedBranch}&doctorId=${resolvedDoctorId}`)
      .then(res => setAvailableDates(Array.isArray(res.data.result) ? res.data.result : []))
      .catch(err => console.error(err))
  }, [resolvedDoctorId, selectedBranch])
 
  useEffect(() => {
    if (!resolvedDoctorId || !selectedBranch || !selectedDate) return
    const formattedDate = format(selectedDate, "yyyy-MM-dd")
    api.get(`/apis/web/doctavailtimeslots?clnorgcode=${selectedBranch}&doctorId=${resolvedDoctorId}&date=${formattedDate}`)
      .then(res => {
        const data = res.data;
        if (data.status === 0 && Array.isArray(data.result)) {
          setTimeSlots(data.result)
        } else {
          console.warn("Invalid slots response:", data)
          setTimeSlots([])
        }
      })
      .catch(err => console.error(err))
  }, [resolvedDoctorId, selectedBranch, selectedDate])

  
 
 
const allDates = availableDates.map((item) => new Date(item.dateTime + "T00:00:00"))
  const DATES_PER_PAGE = 5
  const dates = allDates.slice(dateOffset * DATES_PER_PAGE, (dateOffset + 1) * DATES_PER_PAGE)
 
 const slots = timeSlots || []
 
const handleSlotSelect = (slotId: string) => {
    if (loggedInUser) {
      setShowPatientSelectPopup(true)
    } else {
      setShowPhonePopup(true)
    }
  }
 
const handlePhoneConfirm = async () => {
  if (!mobileNumber || mobileNumber.length !== 10 || !/^[6-9]\d{9}$/.test(mobileNumber)) {
    setPhoneError("Please enter a valid 10-digit mobile number")
    return
  }
  setPhoneError("")

  try {
    const res = await api.post("/apis/web/checkPatientAndSendOTP", new URLSearchParams({ 
      mobileNo: `91${mobileNumber}` 
    }), {
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
    });

    const data = res.data;
    if (data.status === 0) {
      setSignupMode(false)
      setTimer(30)
      setShowPhonePopup(false)
      setShowOtpPopup(true)
    } else if (data.status === 2) {
      setShowNotFoundPopup(true)
    } else {
      alert(data.message || "Failed to send OTP")
    }

  } catch (e) {
    alert("Network error. Please try again.")
  }
}

const handleRegisterYes = async () => {
  try {
    const res = await fetch("/apis/web/sendotp", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ mobileNo: mobileNumber }),
    })
    const data = await res.json()
    if (data.status === 0) {
      setShowNotFoundPopup(false)
      setSignupMode(true)
      setTimer(30)
      setShowPhonePopup(false)
      setShowOtpPopup(true)
    } else {
      alert(data.message || "Failed to send OTP")
    }
  } catch (e) {
    alert("Failed to send OTP")
  }
}

  const handleResendOTP = async () => {
  if (signupMode) {
    try {
      await fetch("/apis/web/sendotp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mobileNo: mobileNumber }),
      })
      setTimer(30)
    } catch (error) {
      alert("Failed to resend OTP")
    }
    return
  }
  try {
    await api.post("/apis/web/checkPatientAndSendOTP", new URLSearchParams({ 
      mobileNo: `91${mobileNumber}` 
    }), {
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
    });
    setTimer(30)
  } catch (error) {
    alert("Failed to resend OTP")
  }
}
 
  const handleAddNewPatient = () => {
    setShowRegistrationPopup(true)
  }
 
  const handleRegistrationSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      const userStr = getCookie("user")
      let mobile = mobileNumber
      if (!mobile && userStr && userStr !== "undefined") {
        try {
          const u = JSON.parse(userStr)
          const ph = u?.mobile || u?.phone || u?.mobileNo || ""
          mobile = ph.replace(/\D/g, "").replace(/^91/, "")
        } catch (e) {}
      }
      const genderMap: Record<string, string> = { Male: "M", Female: "F", Other: "O" }

      if (signupMode) {
        // New user signup - use /apis/web/signup
        const res = await fetch("/apis/web/signup", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            FirstName: registrationData.firstName,
            LastName: registrationData.lastName,
            Dob: registrationData.dob,
            Gender: genderMap[registrationData.gender] || registrationData.gender,
            Mobile: mobile,
            Relation: "000",
            stateId: registrationData.stateId,
            CountryMobileCode: "+91",
            country: "India",
          }),
        })
        const data = await res.json()
        if (data.status === 0) {
          const userData = data.user || {
            name: `${registrationData.firstName} ${registrationData.lastName}`,
            mobile: mobile,
            patientId: data.mrno || "",
          }
          if (data.accessToken) setCookie("accessToken", data.accessToken, 1)
          if (data.refreshToken) setCookie("refreshToken", data.refreshToken, 7)
          setCookie("user", JSON.stringify(userData), 7)
          setLoggedInUser(userData)
          window.dispatchEvent(new CustomEvent("user-updated", { detail: userData }))

          const patients = await fetchPatientsByMobile(mobile)
          setSavedPatients(patients)
          setShowRegistrationPopup(false)
          setShowPatientSelectPopup(true)
          setRegistrationData({ firstName: "", lastName: "", dob: "", relation: "", gender: "Male", stateId: "" })
          setSignupMode(false)
        } else {
          alert(data.message || "Registration failed")
        }
      } else {
        // Add new patient - use /apis/web/registerpatient
        const res = await api.post("/apis/web/registerpatient", {
          FirstName: registrationData.firstName,
          LastName: registrationData.lastName,
          Dob: registrationData.dob,
          Gender: genderMap[registrationData.gender] || registrationData.gender,
          Mobile: mobile,
          Relation: registrationData.relation,
          stateId: registrationData.stateId,
          CountryMobileCode: "+91",
          country: "India",
        })
        const data = res.data
        if (data.success || data.status === 0) {
          const patients = await fetchPatientsByMobile(mobile)
          setSavedPatients(patients)
          setShowRegistrationPopup(false)
          setRegistrationData({ firstName: "", lastName: "", dob: "", relation: "", gender: "Male", stateId: "" })
        } else {
          alert(data.message || "Registration failed")
        }
      }
    } catch (error) {
      console.error(error)
      alert("Registration failed")
    }
  }
 
  const handleOtpConfirm = async () => {
    if (!otpValue || otpValue.length < 4) {
      alert("Please enter a valid OTP")
      return
    }
    setOtpError("")

    if (signupMode) {
      try {
        const res = await fetch("/apis/web/verifysignupOTP", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ mobileNo: mobileNumber, otp: otpValue }),
        })
        const data = await res.json()
        if (data.status === 0) {
          setShowOtpPopup(false)
          setOtpValue("")
          setShowRegistrationPopup(true)
        } else {
          setOtpError(data.message || "Invalid OTP")
        }
      } catch (e) {
        setOtpError("Something went wrong")
      }
      return
    }

    try {
      const res = await api.post("/apis/web/verifyOTP", new URLSearchParams({ 
        mobileNo: `91${mobileNumber}`, 
        otp: otpValue 
      }), {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      });

      const data = res.data;
      if (data.status === 0) {
        setCookie("accessToken", data.accessToken, 1)
        setCookie("refreshToken", data.refreshToken, 7)
        setCookie("user", JSON.stringify(data.user), 7)
        setLoggedInUser(data.user)

        const patients = await fetchPatientsByMobile(mobileNumber)
        setSavedPatients(patients)

        window.dispatchEvent(new CustomEvent("user-updated", { detail: data.user }))

        setShowOtpPopup(false)
        setOtpValue("")
        setShowPatientSelectPopup(true)
      } else {
        setOtpError(data.message || "Invalid OTP")
      }
    } catch (e) {
      setOtpError("Network error. Please try again.")
    }
  }
 
 
  const handlePayNow = async () => {
    if (!selectedPatient) {
      alert("Please select a patient first")
      return
    }
    setSelectedPatientForPayment({
      id: selectedPatient.id,
      name: selectedPatient.name,
      uid: selectedPatient.uid,
    })
    setPriceLoading(true)
    try {
      const res = await api.get(`/apis/web/fullconsultationprice?doctorId=${resolvedDoctorId}&mrnumber=${selectedPatient.uid}&hospitalId=${selectedBranch}`, {
        headers: {
          "Content-Type": "application/json",
          "middleware-key": "$2b$10$5FnYj95ETCkrL3gK6p4zIeqigdvkpP8In5x.8K009CbaDgMc4G7VG"
        }
      });
      if (res.data.status === 0 && res.data.result) {
        setConsultationPrice({
          visitType: res.data.result.visitType || "",
          visitTypeName: res.data.result.visitTypeName || "",
          consultationCharge: res.data.result.consultationCharge || 0
        })
      }
    } catch (err) {
      console.error("Failed to fetch consultation price", err)
    }
    setPriceLoading(false)
    setShowPatientSelectPopup(false)
    setShowPaymentPopup(true)
  }
 
  const handlePaymentComplete = async () => {
    if (!consultationPrice || !selectedPatientForPayment) return
    setPayLoading(true)
    setPayMode("online")

    const userStr = getCookie("user")
    let mobile = mobileNumber
    if (!mobile && userStr && userStr !== "undefined") {
      try {
        const u = JSON.parse(userStr)
        const ph = u?.mobile || u?.phone || u?.mobileNo || ""
        mobile = ph.replace(/\D/g, "").replace(/^91/, "")
      } catch (e) {}
    }

    const details = {
      doctorCode: resolvedDoctorId,
      doctorName: selectedDoctor?.name,
      patientId: selectedPatientForPayment.uid,
      patientName: selectedPatientForPayment.name,
      amount: consultationPrice.consultationCharge,
      appointmentDate: selectedDate ? format(selectedDate, "yyyy-MM-dd") : "",
      appointmentTime: consultationType === "video" ? "00:00" : selectedSlotRaw,
      visitType: consultationPrice.visitType,
      visitTypeName: consultationPrice.visitTypeName,
      consType: consultationType === "video" ? "006" : "001",
      clnorgcode: selectedBranch,
      hospitalName: branches.find(b => b.id === selectedBranch)?.name || "",
      userId: mobile,
      paymentStatus: "Pending",
    }
    setPayDetails(details)

    try {
      const res = await api.post("/apis/web/createorder", {
        amount: consultationPrice.consultationCharge,
        patientId: selectedPatientForPayment.uid,
        doctorId: resolvedDoctorId,
      })
      const data = res.data
      if (data.status === 0 && data.orderId) {
        const options = {
          key: process.env.REACT_APP_RAZORPAY_KEY,
          name: "Pranaam Hospitals",
          description: `Consultation - ${details.doctorName}`,
          image: "/images/pranaam-logo.png",
          order_id: data.orderId,
          prefill: {
            name: details.patientName,
            contact: details.userId,
          },
          handler: async function (response: any) {
            const finalDetails = {
              ...details,
              consultationType: consultationType,
              speciality: selectedDoctor?.specialty || selectedDoctor?.designation || "",
              razorpayPaymentId: response.razorpay_payment_id,
              razorpayOrderId: response.razorpay_order_id,
              razorpaySignature: response.razorpay_signature,
              orderId: data.orderId,
              paymentStatus: "Paid",
              currency: data.currency || "INR",
              mobile: (() => { try { const u = JSON.parse(getCookie("user") || "{}"); return u?.mobile || u?.phone || u?.mobileNo || "" } catch(e) { return "" } })(),
              userId: (() => { try { const u = JSON.parse(getCookie("user") || "{}"); return u?.patientId || u?.userId || "" } catch(e) { return "" } })(),
            }
            sessionStorage.removeItem("payDetails")
            try {
              const apptRes = await api.post("/apis/web/createappointment", {
                doctorCode: finalDetails.doctorCode,
                doctorName: finalDetails.doctorName,
                patientId: finalDetails.patientId,
                patientName: finalDetails.patientName,
                amount: finalDetails.amount,
                appointmentDate: finalDetails.appointmentDate,
                appointmentTime: finalDetails.appointmentTime,
                visit_type: finalDetails.visitType,
                consType: finalDetails.consType,
                clnorgcode: finalDetails.clnorgcode,
                hospitalName: finalDetails.hospitalName,
                userId: finalDetails.userId,
                mobile: finalDetails.mobile,
                razorpayPaymentId: finalDetails.razorpayPaymentId,
                razorpayOrderId: finalDetails.razorpayOrderId,
                razorpaySignature: finalDetails.razorpaySignature,
                orderId: finalDetails.orderId,
                paymentStatus: "Paid",
              })
              const apptData = apptRes.data
              if (apptData.success) {
                finalDetails.appointmentNo = apptData.appointmentNo || ""
                finalDetails.billNo = apptData.billNo || ""
              }
            } catch (err) {
              console.error("Create appointment failed:", err)
            }
            setPayDetails(finalDetails)
            setShowPaymentPopup(false)
            setShowThankYou(true)
          },
          notes: {
            patientId: details.patientId,
            doctorCode: details.doctorCode,
            appointmentDate: details.appointmentDate,
            appointmentTime: details.appointmentTime,
          },
          modal: {
            ondismiss: function () {
              alert("Payment failed. Please try again.")
              setPayLoading(false)
            },
            confirm_close: true,
          },
          retry: {
            enabled: true,
            max_count: 3,
          },
          timeout: 300,
          theme: {
            color: "#dc2626",
          },
        }

        // Store payDetails in sessionStorage as fallback for redirect
        sessionStorage.setItem("payDetails", JSON.stringify({
          ...details,
          consultationType: consultationType,
          speciality: selectedDoctor?.specialty || selectedDoctor?.designation || "",
          orderId: data.orderId,
          currency: data.currency || "INR",
        }))

        const rzp = new (window as any).Razorpay(options)
        rzp.on('payment.failed', function (response: any) {
          alert(response.error?.description || "Payment failed. Please try again.")
          setPayLoading(false)
        })
        rzp.open()
      } else {
        alert(data.message || "Failed to create order")
      }
    } catch (err) {
      console.error("Create order failed:", err)
      alert("Payment failed. Please try again.")
    }
    setPayLoading(false)
  }
 
  const handlePayAtHospital = async () => {
    if (!consultationPrice || !selectedPatientForPayment) return
    setPayLoading(true)
    setPayMode("hospital")

    const userStr = getCookie("user")
    let mobile = mobileNumber
    if (!mobile && userStr && userStr !== "undefined") {
      try {
        const u = JSON.parse(userStr)
        const ph = u?.mobile || u?.phone || u?.mobileNo || ""
        mobile = ph.replace(/\D/g, "").replace(/^91/, "")
      } catch (e) {}
    }

    const details: any = {
      doctorCode: resolvedDoctorId,
      doctorName: selectedDoctor?.name,
      patientId: selectedPatientForPayment.uid,
      patientName: selectedPatientForPayment.name,
      amount: consultationPrice.consultationCharge,
      appointmentDate: selectedDate ? format(selectedDate, "yyyy-MM-dd") : "",
      appointmentTime: consultationType === "video" ? "00:00" : selectedSlotRaw,
      visitType: consultationPrice.visitType,
      visitTypeName: consultationPrice.visitTypeName,
      consType: consultationType === "video" ? "006" : "001",
      clnorgcode: selectedBranch,
      hospitalName: branches.find(b => b.id === selectedBranch)?.name || "",
      consultationType: consultationType,
      speciality: selectedDoctor?.specialty || selectedDoctor?.designation || "",
      mobile: (() => { try { const u = JSON.parse(getCookie("user") || "{}"); return u?.mobile || u?.phone || u?.mobileNo || "" } catch(e) { return "" } })(),
      userId: (() => { try { const u = JSON.parse(getCookie("user") || "{}"); return u?.patientId || u?.userId || "" } catch(e) { return "" } })(),
      razorpayPaymentId: "",
      razorpayOrderId: "",
      razorpaySignature: "",
      paymentStatus: "Pending",
    }

    try {
      const orderRes = await api.post("/apis/web/createorder", {
        amount: consultationPrice.consultationCharge,
        patientId: selectedPatientForPayment.uid,
        doctorId: resolvedDoctorId,
      })
      const orderData = orderRes.data
      if (orderData.status === 0 && orderData.orderId) {
        details.orderId = orderData.orderId
        details.currency = orderData.currency || "INR"
      }
    } catch (err) {
      console.error("Create order failed:", err)
    }

    try {
      const apptRes = await api.post("/apis/web/createappointment", {
        doctorCode: details.doctorCode,
        doctorName: details.doctorName,
        patientId: details.patientId,
        patientName: details.patientName,
        amount: details.amount,
        appointmentDate: details.appointmentDate,
        appointmentTime: details.appointmentTime,
        visit_type: details.visitType,
        consType: details.consType,
        clnorgcode: details.clnorgcode,
        hospitalName: details.hospitalName,
        userId: details.userId,
        mobile: details.mobile,
        razorpayPaymentId: "",
        razorpayOrderId: "",
        razorpaySignature: "",
        orderId: details.orderId || "",
        paymentStatus: "Pending",
      })
      const apptData = apptRes.data
      if (apptData.success) {
        details.appointmentNo = apptData.appointmentNo || ""
        details.billNo = apptData.billNo || ""
      }
    } catch (err) {
      console.error("Create appointment failed:", err)
    }

    setPayDetails(details)
    setShowPaymentPopup(false)
    setShowThankYou(true)
    setPayLoading(false)
  }
 
  if (showThankYou) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center py-8 px-4">
        <div className="max-w-3xl w-full mx-auto">
          <div className="text-center mb-6">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">{payDetails?.paymentStatus === "Paid" ? "Payment Successful" : "Appointment Confirmed"}</h1>
            <p className="text-gray-600">Your appointment has been successfully booked</p>
          </div>
 
          <div id="print-confirmation">
          <div className="bg-white rounded-xl shadow-lg p-6 md:p-8 mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6 pb-3 border-b">Appointment Details</h2>
            <div className="space-y-4">
              <div className="flex justify-between items-start">
                <span className="text-gray-600 font-medium">Patient Name</span>
                <span className="font-semibold text-gray-900 text-right">{payDetails?.patientName || selectedPatientForPayment?.name}</span>
              </div>
              <div className="flex justify-between items-start">
                <span className="text-gray-600 font-medium">Patient ID</span>
                <span className="font-semibold text-primary text-right">{payDetails?.patientId || selectedPatientForPayment?.uid}</span>
              </div>
              <div className="flex justify-between items-start">
                <span className="text-gray-600 font-medium">Doctor</span>
                <span className="font-semibold text-gray-900 text-right">{payDetails?.doctorName || selectedDoctor?.name}</span>
              </div>
              <div className="flex justify-between items-start">
                <span className="text-gray-600 font-medium">Speciality</span>
                <span className="font-semibold text-gray-900 text-right">{selectedDoctor?.specialty || selectedDoctor?.designation}</span>
              </div>
              <div className="flex justify-between items-start">
                <span className="text-gray-600 font-medium">Consultation Type</span>
                <span className="font-semibold text-gray-900 text-right">{consultationType === "video" ? "Video Consultation" : "Hospital Visit"}</span>
              </div>
              <div className="flex justify-between items-start">
                <span className="text-gray-600 font-medium">Date</span>
                <span className="font-semibold text-gray-900 text-right">{payDetails?.appointmentDate || (selectedDate && format(selectedDate, "dd MMM yyyy"))}</span>
              </div>
              {(payDetails?.appointmentTime || selectedSlot) && (
                <div className="flex justify-between items-start">
                  <span className="text-gray-600 font-medium">Time</span>
                  <span className="font-semibold text-gray-900 text-right">{payDetails?.appointmentTime || selectedSlot}</span>
                </div>
              )}
              <div className="flex justify-between items-start">
                <span className="text-gray-600 font-medium">Location</span>
                <span className="font-semibold text-gray-900 text-right">{payDetails?.hospitalName || branches.find((b) => b.id === selectedBranch)?.name || selectedBranch}</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6 md:p-8 mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6 pb-3 border-b">Payment Details</h2>
            <div className="space-y-4">
              {payDetails?.visitTypeName && (
                <div className="flex justify-between items-start">
                  <span className="text-gray-600 font-medium">Visit Type</span>
                  <span className="font-semibold text-gray-900 text-right">{payDetails.visitTypeName}</span>
                </div>
              )}
              <div className="flex justify-between items-start">
                <span className="text-gray-600 font-medium">{payDetails?.paymentStatus === "Paid" ? "Amount Paid" : "Amount Due"}</span>
                <span className={`font-bold text-right ${payDetails?.paymentStatus === "Paid" ? "text-green-600" : "text-orange-500"}`}>Rs. {payDetails?.amount || 0}/-</span>
              </div>
              {payDetails?.orderId && (
                <div className="flex justify-between items-start">
                  <span className="text-gray-600 font-medium">Order ID</span>
                  <span className="font-semibold text-gray-900 text-right text-xs">{payDetails.orderId}</span>
                </div>
              )}
              {payDetails?.razorpayPaymentId && (
                <div className="flex justify-between items-start">
                  <span className="text-gray-600 font-medium">Payment ID</span>
                  <span className="font-semibold text-gray-900 text-right text-xs">{payDetails.razorpayPaymentId}</span>
                </div>
              )}
              {payDetails?.appointmentNo && (
                <div className="flex justify-between items-start">
                  <span className="text-gray-600 font-medium">Appointment No</span>
                  <span className="font-semibold text-gray-900 text-right">{payDetails.appointmentNo}</span>
                </div>
              )}
              {payDetails?.billNo && (
                <div className="flex justify-between items-start">
                  <span className="text-gray-600 font-medium">Bill No</span>
                  <span className="font-semibold text-gray-900 text-right">{payDetails.billNo}</span>
                </div>
              )}
              <div className="flex justify-between items-start">
                <span className="text-gray-600 font-medium">Status</span>
                <span className={`font-semibold text-right ${payDetails?.paymentStatus === "Paid" ? "text-green-600" : "text-orange-500"}`}>{payDetails?.paymentStatus === "Paid" ? "Paid" : "Pending"}</span>
              </div>
            </div>
          </div>
          </div>
 
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              size="lg"
              variant="outline"
              className="w-full sm:flex-1"
              onClick={() => {
                const isPaid = payDetails?.paymentStatus === "Paid"
                const heading = isPaid ? "Payment Successful" : "Appointment Confirmed"
                const amountLabel = isPaid ? "Amount Paid" : "Amount Due"
                const amountColor = isPaid ? "#16a34a" : "#f97316"
                const statusText = isPaid ? "Paid" : "Pending"

                const row = (label: string, value: string, opts?: { color?: string; small?: boolean }) => {
                  if (!value) return ""
                  const style = opts?.color ? `color:${opts.color};` : ""
                  const fontSize = opts?.small ? "font-size:11px;" : ""
                  return `<div style="display:flex;justify-content:space-between;align-items:center;padding:6px 0;border-bottom:1px solid #f0f0f0"><span style="color:#6b7280;font-size:13px">${label}</span><span style="font-weight:600;color:#111;text-align:right;font-size:13px;${style}${fontSize}">${value}</span></div>`
                }

                const apptRows = [
                  row("Patient Name", payDetails?.patientName || ""),
                  row("Patient ID", payDetails?.patientId || "", { color: "#dc2626" }),
                  row("Doctor", payDetails?.doctorName || ""),
                  row("Speciality", payDetails?.speciality || selectedDoctor?.specialty || ""),
                  row("Consultation Type", payDetails?.consultationType === "video" ? "Video Consultation" : "Hospital Visit"),
                  row("Date", payDetails?.appointmentDate || ""),
                  payDetails?.appointmentTime && payDetails.appointmentTime !== "00:00" ? row("Time", payDetails.appointmentTime) : "",
                  row("Location", payDetails?.hospitalName || ""),
                ].join("")

                const payRows = [
                  payDetails?.visitTypeName ? row("Visit Type", payDetails.visitTypeName) : "",
                  row(amountLabel, `Rs. ${payDetails?.amount || 0}/-`, { color: amountColor }),
                  payDetails?.orderId ? row("Order ID", payDetails.orderId, { small: true }) : "",
                  payDetails?.razorpayPaymentId ? row("Payment ID", payDetails.razorpayPaymentId, { small: true }) : "",
                  payDetails?.appointmentNo ? row("Appointment No", payDetails.appointmentNo) : "",
                  payDetails?.billNo ? row("Bill No", payDetails.billNo) : "",
                  row("Status", statusText, { color: amountColor }),
                ].join("")

                const html = `<html><head><title>${heading}</title><style>@page{margin:10mm}@media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact}}</style></head><body style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;margin:0;padding:0;background:#fff;color:#111">
                  <div style="max-width:700px;margin:0 auto;padding:10px 16px">
                    <div style="text-align:center;margin-bottom:12px">
                      <div style="width:48px;height:48px;border-radius:50%;background:#dcfce7;display:inline-flex;align-items:center;justify-content:center;margin-bottom:8px">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#16a34a" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 13l4 4L19 7"/></svg>
                      </div>
                      <h1 style="font-size:22px;font-weight:700;margin:0 0 2px">${heading}</h1>
                      <p style="color:#6b7280;margin:0;font-size:12px">Your appointment has been successfully booked</p>
                    </div>
                    <div style="background:#fff;border:1px solid #e5e7eb;border-radius:8px;padding:14px 18px;margin-bottom:10px">
                      <h2 style="font-size:15px;font-weight:700;margin:0 0 8px;padding-bottom:6px;border-bottom:1px solid #e5e7eb">Appointment Details</h2>
                      ${apptRows}
                    </div>
                    <div style="background:#fff;border:1px solid #e5e7eb;border-radius:8px;padding:14px 18px">
                      <h2 style="font-size:15px;font-weight:700;margin:0 0 8px;padding-bottom:6px;border-bottom:1px solid #e5e7eb">Payment Details</h2>
                      ${payRows}
                    </div>
                  </div>
                </body></html>`

                const win = window.open("", "_blank")
                if (!win) return
                win.document.write(html)
                win.document.close()
                win.print()
              }}
            >
              Print
            </Button>
            <Button
              size="lg"
              className="w-full sm:flex-1"
              onClick={() => {
                setShowThankYou(false)
                setSelectedDoctor(null)
                setSelectedPatient(null)
                setSelectedPatientForPayment(null)
                setSelectedDate(null)
                setSelectedSlot("")
                setShowPatientSelectPopup(false)
                setShowPaymentPopup(false)
                setShowOtpPopup(false)
                setPayDetails(null)
                navigate("/book-now")
              }}
            >
              Book Another Appointment
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="w-full sm:flex-1"
              onClick={() => navigate("/")}
            >
              Go to Home
            </Button>
          </div>
        </div>
      </div>
    )
  }
 
  if (!selectedDoctor) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p>Loading...</p>
      </div>
    )
  }
 
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Doctors
          </Button>
        </div>
      </div>
 
      <div className="container mx-auto px-4 py-4 md:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 lg:gap-8">
          {/* Left Column - Doctor Info & Branch Selection */}
          <div className="space-y-4 md:space-y-6">
            {/* Doctor Card */}
            <div className="bg-white rounded-xl shadow-sm p-4 md:p-6">
              <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
                <div className="relative w-24 h-24 sm:w-32 sm:h-32 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0 mx-auto sm:mx-0 flex items-center justify-center">
                  {selectedDoctor.image && selectedDoctor.image !== "/placeholder.svg" ? (
                    <img
                      src={selectedDoctor.image}
                      alt={selectedDoctor.name}
                      className="object-cover w-full h-full"
                      onError={(e: any) => { e.target.style.display = "none"; e.target.parentElement.querySelector(".fallback-icon")?.classList.remove("hidden") }}
                    />
                  ) : null}
                  <User className={`w-12 h-12 text-gray-400 fallback-icon ${selectedDoctor.image && selectedDoctor.image !== "/placeholder.svg" ? "hidden" : ""}`} />
                </div>
                <div className="text-center sm:text-left">
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900">{selectedDoctor.name}</h2>
                  <p className="text-primary font-medium text-sm sm:text-base">{selectedDoctor.designation}</p>
                  <p className="text-gray-600 text-xs sm:text-sm mt-1">{selectedDoctor.qualifications}</p>
                 
                  <div className="mt-3 sm:mt-4 space-y-2">
                    {selectedDoctor.languages.length > 0 && (
                    <div className="flex items-center justify-center sm:justify-start gap-2 text-xs sm:text-sm text-gray-600">
                      <Globe className="w-4 h-4" />
                      {selectedDoctor.languages.join(", ")}
                    </div>
                    )}
                    <div className="flex items-center justify-center sm:justify-start gap-2 text-xs sm:text-sm text-gray-600">
                      <MapPin className="w-4 h-4" />
                     {branches.find(b => b.id === selectedBranch)?.name || "No Branch Selected"}
                    </div>
                  </div>
                </div>
              </div>
            </div>
 
            {/* Book Appointment Selection */}
            <div className="bg-white rounded-xl shadow-sm p-4 md:p-6">
              <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-4 md:mb-6">Book an appointment</h3>
             
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 mb-4 md:mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Select Branch</label>
                  <Select value={selectedBranch} onValueChange={setSelectedBranch}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select Branch" />
                    </SelectTrigger>
                    <SelectContent>
                      {branches.map((b) => (
                        <SelectItem key={b.id} value={b.id}>
                          {b.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Select Speciality</label>
                  <Select value={selectedDoctor.specialtyId} disabled>
                    <SelectTrigger>
                      <SelectValue>{selectedDoctor.specialty}</SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={selectedDoctor.specialtyId}>
                        {selectedDoctor.specialty}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
 
              <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
                <Button
                  className="flex-1"
                  variant={consultationType === "hospital" ? "default" : "outline"}
                  onClick={() => setConsultationType("hospital")}
                >
                  <Building2 className="w-4 h-4 mr-2" />
                  Hospital Visit
                </Button>
                <Button
                  className="flex-1"
                  variant={consultationType === "video" ? "default" : "outline"}
                  onClick={() => setConsultationType("video")}
                >
                  <Globe className="w-4 h-4 mr-2" />
                  Video Consultation
                </Button>
              </div>
            </div>
          </div>
 
          {/* Right Column - Date Selection & Slots */}
          <div className="space-y-4 md:space-y-6">
            <div className="bg-white rounded-xl shadow-sm p-4 md:p-6">
              {consultationType === "video" ? (
                <div className="text-center py-8 md:py-12">
                  <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-4">For Appointment Call</h3>
                  <p className="text-base md:text-lg font-semibold text-primary">040 23042777, 040 23043777, 040 35094777</p>
                </div>
              ) : (
                <>
              <h3 className="text-base md:text-lg font-bold text-gray-900 mb-3 md:mb-4">Available slots</h3>
             
              {/* Date Picker */}
              <div className="flex items-center gap-1 md:gap-2 mb-4 md:mb-6 pb-3 md:pb-4">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setDateOffset(Math.max(0, dateOffset - 1))}
                  disabled={dateOffset === 0}
                >
                  <ChevronLeft className="w-5 h-5" />
                </Button>
               
                <div className="flex-1 flex gap-1 md:gap-2 overflow-x-auto pb-2 scrollbar-hide">
                  {dates.map((date, index) => {
                      const isSelected =
                        selectedDate && selectedDate.toDateString() === date.toDateString()
 
                      const isToday =
                        date.toDateString() === new Date().toDateString()
 
                      return (
                        <button
                          key={index}
                          onClick={() => setSelectedDate(date)}
                          className={`px-3 md:px-4 py-1.5 md:py-2 rounded-full text-xs md:text-sm font-medium whitespace-nowrap transition-colors flex-shrink-0 ${
                            isSelected
                              ? "bg-primary text-white"
                              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                          }`}
                        >
                          {isToday ? "Today" : format(date, "EEE")}, {format(date, "dd MMM")}
                        </button>
                      )
                    })}
                </div>
               
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setDateOffset(dateOffset + 1)}
                  disabled={((dateOffset + 1) * DATES_PER_PAGE) >= allDates.length}
                >
                  <ChevronRight className="w-5 h-5" />
                </Button>
              </div>
 
              {/* Time Slots */}
              {selectedDate ? (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-4 md:mb-6">
                    {slots.length === 0 ? (
                      <p className="text-gray-500 col-span-full text-center">
                        No slots available
                      </p>
                    ) : (
                      slots.map((slot, index) => {
                        const time = formatTime(slot.slottime)
                        const rawTime = slot.slottime?.substring(0, 5) || slot.slottime

                        return (
                          <button
                            key={slot.slno || index}
                            disabled={slot.isavailable !== "Y"}
                            onClick={() => { setSelectedSlot(time); setSelectedSlotRaw(rawTime); handleSlotSelect(time) }}
                            className={`px-2 md:px-3 py-1.5 md:py-2 rounded-lg text-xs md:text-sm font-medium transition-colors ${
                              selectedSlot === time
                                ? "bg-primary text-white"
                                : slot.isavailable === "Y"
                                ? "bg-gray-100 text-gray-700 hover:bg-gray-200"
                                : "bg-gray-200 text-gray-400 cursor-not-allowed"
                            }`}
                          >
                            {time}
                          </button>
                        )
                      })
                    )}
                  </div>
              ) : (
                <p className="text-sm text-gray-500 text-center py-4 mb-4 md:mb-6">
                  Please select a date to view available slots
                </p>
              )}
                </>
              )}
              {/* OLD VIDEO CONSULTATION CODE - commented for future use
              {consultationType === "video" && selectedDate && (
                <div className="text-center py-6 md:py-8 mb-4 md:mb-6">
                  <p className="text-sm md:text-base text-gray-700 font-medium mb-2">Video Consultation Selected</p>
                  <p className="text-xs md:text-sm text-gray-600">Please proceed to confirm your appointment</p>
                  <Button
                    onClick={() => loggedInUser ? setShowPatientSelectPopup(true) : setShowPhonePopup(true)}
                    className="mt-4"
                  >
                    Continue
                  </Button>
                </div>
              )}
              */}
            </div>
          </div>
        </div>
      </div>
 
      {/* Phone Number Popup */}
      <Dialog open={showPhonePopup} onOpenChange={setShowPhonePopup}>
        <DialogContent className="sm:max-w-[550px] p-0 overflow-hidden [&>button]:hidden">
          <div className="relative">
            <button
              onClick={() => setShowPhonePopup(false)}
              className="absolute right-4 top-4 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors z-10"
            >
              <X className="w-4 h-4" />
            </button>
           
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-3">Confirm your appointment</h2>
             
              <div className="mb-2">
                <p className="text-gray-700">
                  {consultationType === "video" ? "Video" : "Physical"} Consultation with{" "}
                  <span className="text-primary font-semibold">{selectedDoctor.name}</span>
                </p>
              </div>
 
              <div className="flex items-center justify-between mb-6">
                <p className="text-gray-700">
                  {selectedDate && format(selectedDate, "dd, MMM yyyy")}
                </p>
                <p className="text-gray-700">
                  {selectedSlot}
                </p>
              </div>
 
              <p className="text-gray-600 text-sm mb-4">
                Please enter the registered mobile number for the specific patient.
              </p>
 
              <div className="flex gap-3 items-center">
                <div className="flex items-center flex-1 border rounded-md overflow-hidden h-11">
                  <div className="flex items-center gap-2 px-3 bg-gray-50 h-full border-r">
                    <img
                      src="https://flagcdn.com/w20/in.png"
                      alt="India"
                      width={20}
                      height={15}
                      className="rounded-sm"
                    />
                    <span className="text-gray-600 text-sm font-medium">+91</span>
                  </div>
                  <Input
                    type="tel"
                    placeholder="Enter Mobile Number"
                    value={mobileNumber}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '').slice(0, 10)
                      setMobileNumber(val)
                      if (phoneError) setPhoneError("")
                    }}
                    className="border-0 h-full focus-visible:ring-0 focus-visible:ring-offset-0"
                    maxLength={10}
                  />
                </div>
                <Button
                  onClick={handlePhoneConfirm}
                  className="px-6 h-11 whitespace-nowrap"
                >
                  Confirm Booking
                </Button>
              </div>
              {phoneError && <p className="text-red-600 text-xs mt-1">{phoneError}</p>}
            </div>
          </div>
        </DialogContent>
      </Dialog>
 
      <Dialog open={showOtpPopup} onOpenChange={setShowOtpPopup}>
        <DialogContent className="sm:max-w-[550px] p-0 overflow-hidden [&>button]:hidden">
          <div className="relative">
            <button
              onClick={() => setShowOtpPopup(false)}
              className="absolute right-4 top-4 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors z-10"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-3">Confirm OTP</h2>
              <div className="mb-2">
                <p className="text-gray-700">
                  {consultationType === "video" ? "Video" : "Physical"} Consultation with{" "}
                  <span className="text-primary font-semibold">{selectedDoctor.name}</span>
                </p>
              </div>
              <div className="flex items-center justify-between mb-4">
                <p className="text-gray-700">{selectedDate && format(selectedDate, "dd, MMM yyyy")}</p>
                <p className="text-gray-700">{selectedSlot}</p>
              </div>
              <p className="text-gray-600 text-sm mb-4">We have sent you an OTP on {mobileNumber}.</p>
              {otpError && <p className="text-red-600 text-sm mb-2 font-medium">{otpError}</p>}
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-800 mb-2">Enter OTP<span className="text-red-500">*</span></label>
                <div className="flex gap-3">
                  <Input
                    type="text"
                    placeholder="Please Enter OTP"
                    value={otpValue}
                    onChange={(e) => setOtpValue(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="flex-1"
                    maxLength={6}
                  />
                  <Button onClick={handleOtpConfirm} className="px-6">Confirm OTP</Button>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-600 text-sm">Still not received OTP?</span>
                <div className="flex items-center gap-2">
                  {timer > 0 ? (
                    <span className="text-gray-500 text-sm">
                      Resend in {timer}s
                    </span>
                  ) : (
                    <Button
                      variant="link"
                      size="sm"
                      className="text-primary p-0 h-auto"
                      onClick={handleResendOTP}
                    >
                      Resend OTP
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
 
      <Dialog open={showPatientSelectPopup} onOpenChange={setShowPatientSelectPopup}>
        <DialogContent className="w-[95vw] sm:max-w-[700px] max-h-[85vh] flex flex-col p-0 [&>button]:hidden">
          <div className="relative flex flex-col min-h-0 h-full">
            <button
              onClick={() => setShowPatientSelectPopup(false)}
              className="absolute right-4 top-4 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors z-10"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="p-4 sm:p-6 overflow-y-auto flex-1 min-h-0">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 pr-8">Confirm your appointment</h2>
              <div className="mb-2">
                <p className="text-gray-700">
                  {consultationType === "video" ? "Video" : "Physical"} Consultation with{" "}
                  <span className="text-primary font-semibold uppercase">{selectedDoctor.name.toUpperCase()}</span>
                </p>
              </div>
              <div className="flex items-center gap-4 mb-2 text-sm">
                <p className="text-gray-700">{selectedDate && format(selectedDate, "dd, MMM yyyy")}</p>
                <p className="text-gray-700">{selectedSlot}</p>
              </div>
              
              <p className="text-gray-600 text-xs sm:text-sm mb-3">Please select the ID or add the new patient to proceed.</p>
 
              <div className="border rounded-lg overflow-x-auto mb-3">
                <table className="w-full min-w-[400px]">
                  <thead className="bg-primary text-white">
                    <tr>
                      <th className="px-3 py-2.5 text-left text-xs font-medium w-12">Select</th>
                      <th className="px-3 py-2.5 text-left text-xs font-medium">Patient Name</th>
                      <th className="px-3 py-2.5 text-left text-xs font-medium">UID</th>
                      <th className="px-3 py-2.5 text-left text-xs font-medium">Reg. Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {savedPatients.map((patient, index) => (
                      <tr
                        key={patient.id}
                        className={`border-t cursor-pointer hover:bg-gray-50 ${selectedPatient?.id === patient.id ? 'bg-primary/5' : ''}`}
                        onClick={() => setSelectedPatient(patient)}
                      >
                        <td className="px-3 py-2.5">
                          <input
                            type="radio"
                            name="patientSelect"
                            checked={selectedPatient?.id === patient.id}
                            onChange={() => setSelectedPatient(patient)}
                            className="w-4 h-4 text-primary accent-primary"
                          />
                        </td>
                        <td className="px-3 py-2.5 text-xs font-medium">{patient.name}</td>
                        <td className="px-3 py-2.5 text-xs text-gray-600">{patient.uid}</td>
                        <td className="px-3 py-2.5 text-xs text-gray-600">{patient.regDate}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
 
            <div className="flex items-center justify-between gap-2 p-4 sm:px-6 sm:pb-6 border-t bg-white flex-shrink-0">
              <Button onClick={handleAddNewPatient} size="sm" className="text-xs sm:text-sm">Add a new patient</Button>
              {selectedPatient && (
                <Button onClick={handlePayNow} size="sm" className="text-xs sm:text-sm">Next</Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
 
      <Dialog open={showPaymentPopup} onOpenChange={setShowPaymentPopup}>
        <DialogContent className="sm:max-w-[450px] max-h-[90vh] overflow-y-auto p-0 [&>button]:hidden">
          <div className="relative">
            <button
              onClick={() => setShowPaymentPopup(false)}
              className="absolute right-2 top-2 w-4 h-4 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors z-10"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="p-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Payment Details</h2>
              <div className="space-y-3 pb-4 border-b">
                <div className="flex justify-between">
                  <span className="text-gray-600">Patient Name</span>
                  <span className="font-semibold text-gray-900">{selectedPatientForPayment?.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">UID</span>
                  <span className="font-semibold text-gray-900">{selectedPatientForPayment?.uid}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Doctor</span>
                  <span className="font-semibold text-gray-900">{selectedDoctor.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Date</span>
                  <span className="font-semibold text-gray-900">{selectedDate && format(selectedDate, "dd MMM yyyy")}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Time</span>
                  <span className="font-semibold text-gray-900">{selectedSlot}</span>
                </div>
              </div>
 
              {priceLoading ? (
                <div className="py-4 text-center text-gray-500">Loading price...</div>
              ) : consultationPrice ? (
                <div className="py-4 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Visit Type</span>
                    <span className="font-semibold text-gray-900">{consultationPrice.visitTypeName}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Consultation Fee</span>
                    <span className="font-semibold text-gray-900">Rs. {consultationPrice.consultationCharge}/-</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t">
                    <span className="font-semibold text-primary">Total Amount</span>
                    <span className="font-bold text-primary">Rs. {consultationPrice.consultationCharge}/-</span>
                  </div>
                </div>
              ) : (
                <div className="py-4 text-center text-red-500 text-sm">Failed to load price</div>
              )}
 
              <div className="flex gap-3">
                <Button
                  onClick={handlePaymentComplete}
                  className="w-full py-6 text-lg"
                  disabled={priceLoading || !consultationPrice || payLoading}
                >
                  {payLoading && payMode === "online" ? "Processing..." : consultationPrice ? `Pay Rs. ${consultationPrice.consultationCharge}/-` : "Pay"}
                </Button>
                <Button
                  variant="outline"
                  onClick={handlePayAtHospital}
                  className="flex-1 py-6 text-lg border-primary text-primary hover:bg-primary/5"
                  disabled={priceLoading || !consultationPrice || payLoading}
                >
                  {payLoading && payMode === "hospital" ? "Processing..." : "Pay at Hospital"}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
 
      <Dialog open={showRegistrationPopup} onOpenChange={setShowRegistrationPopup}>
        <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto p-0 [&>button]:hidden">
          <div className="relative">
            <button
              onClick={() => setShowRegistrationPopup(false)}
              className="absolute right-4 top-4 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors z-10"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Add New Patient</h2>
              <p className="text-gray-600 mb-6">Register a new patient to proceed with the appointment.</p>
              <form onSubmit={handleRegistrationSubmit} className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">First Name<span className="text-red-500">*</span></label>
                    <Input
                      placeholder="First name"
                      value={registrationData.firstName}
                      onChange={(e) => setRegistrationData(prev => ({ ...prev, firstName: e.target.value.replace(/[^a-zA-Z\s]/g, '') }))}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Last Name<span className="text-red-500">*</span></label>
                    <Input
                      placeholder="Last name"
                      value={registrationData.lastName}
                      onChange={(e) => setRegistrationData(prev => ({ ...prev, lastName: e.target.value.replace(/[^a-zA-Z\s]/g, '') }))}
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth<span className="text-red-500">*</span></label>
                  <Input
                    type={registrationData.dob ? "date" : "text"}
                    onFocus={(e: any) => e.target.type = "date"}
                    onBlur={(e: any) => { if (!e.target.value) e.target.type = "text" }}
                    placeholder="Date of Birth"
                    value={registrationData.dob}
                    onChange={(e) => setRegistrationData(prev => ({ ...prev, dob: e.target.value }))}
                    required
                    max={new Date().toISOString().split('T')[0]}
                  />
                </div>
                {!signupMode && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Relation<span className="text-red-500">*</span></label>
                  <select
                    required
                    value={registrationData.relation}
                    onChange={(e) => setRegistrationData(prev => ({ ...prev, relation: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="">Select Relation</option>
                    {relations.map(r => (
                      <option key={r.id} value={r.id}>{r.name}</option>
                    ))}
                  </select>
                </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">State<span className="text-red-500">*</span></label>
                  <select
                    required
                    value={registrationData.stateId}
                    onChange={(e) => setRegistrationData(prev => ({ ...prev, stateId: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="">Select State</option>
                    {states.map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Gender<span className="text-red-500">*</span></label>
                  <Select value={registrationData.gender} onValueChange={(val) => setRegistrationData(prev => ({ ...prev, gender: val }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Male">Male</SelectItem>
                      <SelectItem value="Female">Female</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button type="submit" className="w-full">Submit</Button>
              </form>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {showNotFoundPopup && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center" style={{ pointerEvents: "auto" }}>
          <div className="fixed inset-0 bg-black/80" onClick={() => setShowNotFoundPopup(false)} />
          <div className="relative bg-white rounded-lg w-[90vw] max-w-[350px] p-6" style={{ zIndex: 10000, pointerEvents: "auto" }}>
            <div className="text-center space-y-4">
              <p className="text-sm font-medium text-gray-800">Mobile number not found! Do you want to register?</p>
              <div className="flex justify-center gap-3">
                <button
                  type="button"
                  className="px-6 py-2 border border-gray-300 rounded-md text-sm font-medium hover:bg-gray-50"
                  onClick={() => setShowNotFoundPopup(false)}
                >
                  No
                </button>
                <button
                  type="button"
                  className="px-6 py-2 bg-red-700 hover:bg-red-800 text-white rounded-md text-sm font-medium"
                  onClick={handleRegisterYes}
                >
                  Yes
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}
